import { create } from "zustand";
import { persist } from "zustand/middleware";
import { College } from "../data/colleges";
import { invalidateDashboardCache } from "../hooks/dashboard/useStudentDashboardData";

export interface Filters {
  state: string;
  majors: string[];
  tuitionRange: [number, number];
  inStateOnly: boolean;
  satRange: [number, number];
  actRange: [number, number];
  gpaRange: [number, number];
  size: string[];
  campusType: string[];
}

export interface CollegeStore {
  favorites: string[];
  searchQuery: string;
  activeTab: "all" | "suggested" | "favorites";
  filters: Filters;
  generatedList: {
    reach: College[];
    match: College[];
    safety: College[];
  } | null;
  collegeListCache: {
    params: string;
    data: any[];
    meta: any;
  } | null;

  addFavorite: (collegeId: string) => void;
  removeFavorite: (collegeId: string) => void;
  toggleFavorite: (collegeId: string) => void;
  setFavorites: (favoriteIds: string[]) => void;
  setSearchQuery: (query: string) => void;
  setActiveTab: (tab: "all" | "suggested" | "favorites") => void;
  setFilters: (filters: Partial<Filters>) => void;
  clearFilters: () => void;
  setGeneratedList: (list: {
    reach: College[];
    match: College[];
    safety: College[];
  }) => void;
  setCollegeListCache: (cache: {
    params: string;
    data: any[];
    meta: any;
  } | null) => void;
}

export const defaultFilters: Filters = {
  state: "",
  majors: [],
  tuitionRange: [0, 90000],
  inStateOnly: false,
  satRange: [400, 1600],
  actRange: [0, 36],
  gpaRange: [2.0, 4.0],
  size: [],
  campusType: [],
};

export const useCollegeStore = create<CollegeStore>()(
  persist(
    (set) => ({
      favorites: [],
      searchQuery: "",
      activeTab: "all",
      filters: defaultFilters,
      generatedList: null,
      collegeListCache: null,

      addFavorite: (collegeId) =>
        set((state) => {
          const normalizedId = String(collegeId);
          if (state.favorites.includes(normalizedId)) return state;
          // Invalidate dashboard cache since favorites changed
          invalidateDashboardCache();
          return {
            favorites: [...state.favorites, normalizedId],
          };
        }),

      removeFavorite: (collegeId) =>
        set((state) => {
          const normalizedId = String(collegeId);
          // Invalidate dashboard cache since favorites changed
          invalidateDashboardCache();
          return {
            favorites: state.favorites.filter((id) => id !== normalizedId),
          };
        }),

      toggleFavorite: (collegeId) =>
        set((state) => {
          const normalizedId = String(collegeId);
          const exists = state.favorites.includes(normalizedId);
          // Invalidate dashboard cache since favorites changed
          invalidateDashboardCache();
          return {
            favorites: exists
              ? state.favorites.filter((id) => id !== normalizedId)
              : [...state.favorites, normalizedId],
          };
        }),

      setFavorites: (favoriteIds) => {
        // Invalidate dashboard cache when bulk setting favorites
        invalidateDashboardCache();
        set({
          favorites: favoriteIds.map(String),
        });
      },

      setSearchQuery: (query) => set({ searchQuery: query }),

      setActiveTab: (tab) => set({ activeTab: tab }),

      setFilters: (newFilters) =>
        set((state) => ({
          filters: { ...state.filters, ...newFilters },
        })),

      clearFilters: () => set({ filters: defaultFilters }),

      setGeneratedList: (list) => set({ generatedList: list }),

      setCollegeListCache: (cache) => set({ collegeListCache: cache }),
    }),
    {
      name: "college-storage",
      // Only persist non-favorites data - favorites come from Supabase (single source of truth)
      // NOTE: collegeListCache is intentionally NOT persisted (in-memory only) to avoid localStorage limits
      partialize: (state) => ({
        searchQuery: state.searchQuery,
        activeTab: state.activeTab,
        filters: state.filters,
        generatedList: state.generatedList,
      }),
      // Merge function to ensure old localStorage data gets updated with new default fields
      merge: (
        persistedState: unknown,
        currentState: CollegeStore,
      ): CollegeStore => {
        // Type guard for persisted state
        const persisted = persistedState as Partial<CollegeStore> | null;
        // If persisted state exists, merge it with current state, ensuring all required fields exist
        if (persisted && persisted.filters) {
          return {
            ...currentState,
            ...persisted,
            // Always start with empty favorites - they will be loaded from Supabase
            favorites: [],
            filters: {
              ...defaultFilters,
              ...persisted.filters,
            },
          };
        }
        return currentState;
      },
    },
  ),
);
